/**
 * Combined menu + mask-code search.
 * Priority: exact mask → partial mask → existing menu-text relevance.
 */

import { menuItems } from './menuItems';
import {
  filterMenuItemsByQuery,
  isItemVisible,
} from './menuSearch';
import {
  getMaskRegistry,
  isMaskCodeQuery,
  isMaskEntryVisible,
  normalizeMaskCode,
} from './maskRegistry';

export function formatMaskResultLabel(maskCode, translatedTitle) {
  const code = String(maskCode || '').toUpperCase();
  const title = String(translatedTitle || '').trim();
  if (!code) return title;
  if (!title) return code;
  return `${code} — ${title}`;
}

export function resolveMaskDisplayTitle(entry, t) {
  const tr = typeof t === 'function' ? t : (k) => k;
  if (!entry) return '';
  const key = entry.translationKey || entry.tabTranslationKey || entry.page;
  const translated = tr(key);
  if (translated && translated !== key) return translated;
  if (entry.tabTranslationKey) {
    const tab = tr(entry.tabTranslationKey);
    if (tab && tab !== entry.tabTranslationKey) return tab;
  }
  return translated || key || entry.maskCode;
}

/**
 * Rank mask hits for a query. Permissions applied.
 * @returns {{ exact: object|null, partial: object[] }}
 */
export function searchMaskEntries(query, userPermissions, registry = getMaskRegistry()) {
  const q = normalizeMaskCode(query);
  if (!q || !isMaskCodeQuery(q)) {
    return { exact: null, partial: [] };
  }

  const visible = [];
  for (const entry of registry.values()) {
    if (!isMaskEntryVisible(entry, userPermissions)) continue;
    visible.push(entry);
  }

  const exact = visible.find((e) => e.maskCode === q) || null;
  const partial = visible
    .filter((e) => e.maskCode !== q && e.maskCode.includes(q))
    .sort((a, b) => a.maskCode.localeCompare(b.maskCode));

  return { exact, partial };
}

function toSyntheticMenuItem(entry, t) {
  const titleKey = entry.translationKey || entry.page;
  return {
    id: `__mask__${entry.maskCode}`,
    title: titleKey,
    page: entry.page,
    mask: entry.maskCode,
    permission: entry.permission,
    props: entry.props || {},
    _maskSearchHit: true,
    _maskDisplayCode: String(entry.maskCode).toUpperCase(),
    _registryEntry: entry,
    _searchLabel: formatMaskResultLabel(entry.maskCode, resolveMaskDisplayTitle(entry, t)),
  };
}

/**
 * Annotate matching leaves so Menu can show "CODE — Title" during mask queries.
 */
function annotateMaskLabels(items, query, t) {
  if (!isMaskCodeQuery(query)) return items;
  const q = normalizeMaskCode(query);
  return (items || []).map((item) => {
    if (item?.children?.length) {
      return { ...item, children: annotateMaskLabels(item.children, query, t) };
    }
    if (!item?.mask) return item;
    const code = String(item.mask).toLowerCase();
    if (code === q || code.includes(q)) {
      const title = typeof t === 'function' ? t(item.title || item.page) : (item.title || item.page);
      return {
        ...item,
        _maskSearchHit: true,
        _maskDisplayCode: code.toUpperCase(),
        _searchLabel: formatMaskResultLabel(code, title),
      };
    }
    return item;
  });
}

/**
 * Combined search tree for the sidebar.
 * Mask hits (exact then partial) that are NOT already in the text/mask-annotated tree
 * are prepended as synthetic leaves. Text search tree is preserved.
 */
export function buildCombinedSearchTree(query, t, userPermissions, items = menuItems) {
  const q = String(query || '').trim();
  if (!q) return items;

  const textTree = filterMenuItemsByQuery(items, q, t);
  const annotatedText = annotateMaskLabels(textTree, q, t);

  // Also include menu leaves whose mask matches even when title does not (mask-mode).
  let maskAugmentedTree = annotatedText;
  if (isMaskCodeQuery(q)) {
    const maskOnlyTree = filterMenuItemsByMaskCode(items, q);
    maskAugmentedTree = mergeMenuTrees(annotatedText, annotateMaskLabels(maskOnlyTree, q, t));
  }

  const { exact, partial } = searchMaskEntries(q, userPermissions);
  const priorityCodes = new Set();
  const synthetic = [];
  const pushPriority = (entry) => {
    if (!entry || priorityCodes.has(entry.maskCode)) return;
    priorityCodes.add(entry.maskCode);
    synthetic.push(toSyntheticMenuItem(entry, t));
  };
  pushPriority(exact);
  partial.forEach(pushPriority);

  // Drop duplicate leaves already shown as priority mask hits.
  const restTree = priorityCodes.size
    ? stripLeavesByMaskCodes(maskAugmentedTree, priorityCodes)
    : maskAugmentedTree;

  // Priority: exact/partial synthetic first, then remaining menu/text tree.
  return [...synthetic, ...restTree];
}

/** Remove leaves whose mask is in codes; prune empty folders. */
function stripLeavesByMaskCodes(items, codes) {
  return (items || []).reduce((acc, item) => {
    if (item?.children?.length) {
      const children = stripLeavesByMaskCodes(item.children, codes);
      if (children.length) return [...acc, { ...item, children }];
      return acc;
    }
    const code = item?.mask != null ? String(item.mask).toLowerCase() : '';
    if (code && codes.has(code)) return acc;
    return [...acc, item];
  }, []);
}

/** Filter menu tree by mask code match on leaves (folders kept when children match). */
export function filterMenuItemsByMaskCode(arr, query) {
  const q = normalizeMaskCode(query);
  if (!q) return arr;
  return (arr || []).reduce((acc, item) => {
    if (Array.isArray(item.children) && item.children.length) {
      const filtered = filterMenuItemsByMaskCode(item.children, query);
      if (filtered.length) return [...acc, { ...item, children: filtered }];
      return acc;
    }
    const code = String(item.mask || '').toLowerCase();
    return code === q || code.includes(q) ? [...acc, item] : acc;
  }, []);
}

function mergeMenuTrees(a, b) {
  if (!a?.length) return b || [];
  if (!b?.length) return a || [];
  const byId = new Map();
  const mergeItem = (item) => {
    if (!item) return;
    const key = item.id != null ? String(item.id) : `mask:${item.mask}`;
    const existing = byId.get(key);
    if (!existing) {
      byId.set(key, item.children ? { ...item, children: [...(item.children || [])] } : { ...item });
      return;
    }
    if (item.children?.length || existing.children?.length) {
      const childMap = new Map();
      [...(existing.children || []), ...(item.children || [])].forEach((c) => {
        const ck = c.id != null ? String(c.id) : `mask:${c.mask}`;
        if (!childMap.has(ck)) childMap.set(ck, c);
      });
      byId.set(key, { ...existing, ...item, children: Array.from(childMap.values()) });
    }
  };
  a.forEach(mergeItem);
  b.forEach(mergeItem);
  return Array.from(byId.values());
}

/**
 * Ranked flat results for Enter / tests: exact, then partial, then text leaf actions.
 */
export function rankSearchResults(query, t, userPermissions, items = menuItems) {
  const q = String(query || '').trim();
  const { exact, partial } = searchMaskEntries(q, userPermissions);
  const textTree = filterMenuItemsByQuery(items, q, t);
  const textLeaves = flattenLeaves(textTree).filter((leaf) =>
    isItemVisible(leaf, userPermissions, true),
  );

  const ranked = [];
  const seen = new Set();
  const add = (entry, kind) => {
    if (!entry?.maskCode && !entry?.mask) return;
    const code = normalizeMaskCode(entry.maskCode || entry.mask);
    if (seen.has(code)) return;
    seen.add(code);
    ranked.push({ kind, entry: entry.maskCode ? entry : null, item: entry.maskCode ? null : entry, maskCode: code });
  };

  if (exact) add(exact, 'exact');
  partial.forEach((e) => add(e, 'partial'));
  textLeaves.forEach((leaf) => add(leaf, 'text'));
  return ranked;
}

function flattenLeaves(items, acc = []) {
  for (const item of items || []) {
    if (item?.children?.length) flattenLeaves(item.children, acc);
    else acc.push(item);
  }
  return acc;
}

export function getExactMaskHit(query, userPermissions) {
  const { exact } = searchMaskEntries(query, userPermissions);
  return exact;
}
